
import React, { useState, useEffect, useContext } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Alert from "@mui/material/Alert";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";
import axios from "axios";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";

import PropTypes from "prop-types"; // Import PropTypes

import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import Autocomplete from "@mui/material/Autocomplete";
import MDSnackbar from "components/MDSnackbar";
import MDBox from "components/MDBox";
import TypeBadge from "components/ReportUI/Typebadge";
import MDButton from "components/MDButton";
import DownloadExcelButton from "components/ReportUI/Downloadexcelbutton";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import { AccountsContext } from "context/AccountsContext";

import { stoptimingreportapiendpoint } from "config";
import StopTimingReportTable from "./stopTimingReportTable";
import StopTable from "./stopscell";
import { DialogActions } from "@mui/material";

function StopTimingReport() {
  const [reportData, setReportData] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [successSB, setSuccessSB] = useState(false);
  const [errorSB, setErrorSB] = useState(false);
  const [vehicleIds, setVehicleIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [stopDetails, setStopDetails] = useState([]);

  const { fetchThings, things } = useContext(AccountsContext);

  useEffect(() => {
    fetchThings();
  }, []);

  const today = new Date();
  const [selectedDate, setSelectedDate] = useState(today);

  const openSuccessSB = () => setSuccessSB(true);
  const closeSuccessSB = () => setSuccessSB(false);
  const openErrorSB = () => setErrorSB(true);
  const closeErrorSB = () => setErrorSB(false);

  const columns = [
    { Header: "Date", accessor: "date", width: "10%" },
    { Header: "Vehicle Number", accessor: "thingId", width: "15%" },
    { Header: "Journey Name", accessor: "journeyName", width: "20%" },
    { Header: "Journey Type", accessor: "journeyType", width: "15%" ,
      Cell: ({ value }) => (
        <TypeBadge
          value={value}
          config={{}}
          defaultConfig={{ color: "#1E40AF", bg: "#E9F1FF", icon: null }}
        />
      ),
    },
    { Header: "Journey Time", accessor: "journeyTime", width: "20%" },
    {
      Header: "Number of Stops",
      accessor: "numStops",
      Cell: ({ row }) => {
        const stopCount = row.original.numStops;
        return stopCount ? (
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              setSelectedRow(row.original);
              setStopDetails(row.original.stopsInfo || []);
            }}
            style={{ color: "#1a73e8", textDecoration: "underline" }}
          >
            {stopCount}
          </a>
        ) : (
          "-"
        );
      },
    },
  ];
  const stopColumns = [
    { Header: "Sequence", accessor: "sequence", width: "10%" },
    { Header: "Stop Name", accessor: "fenceName", width: "25%" },
    {
      Header: "Reach Time",
      accessor: "entryTime",
      Cell: ({ value }) =>
        value ? new Date(value).toLocaleString() : "Invalid date",
    },
    {
      Header: "Leave Time",
      accessor: "exitTime",
      Cell: ({ value }) =>
        value ? new Date(value).toLocaleString() : "Invalid date",
    },
    {
      Header: "Time (Hr:Min:Sec)",
      accessor: "timeInside",
      Cell: ({ value }) => (value ? msToTime(value) : "00:00:00"),
    },
  ];
  
  const fetchReport = async () => {
    const token = localStorage.getItem("token");
    setLoading(true);
    setErrorMessage("");

    const formatDate = (d) => {
      const pad = (n) => String(n).padStart(2, "0");
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    };

    try {
      const data = {
        date: formatDate(selectedDate),
        zone: "Asia/Kolkata",
        thingIds: vehicleIds,
      };

      const config = {
        method: "post",
        url: stoptimingreportapiendpoint,
        headers: {
          "x-dhundhoo-session": token,
          "Content-Type": "application/json",
        },
        data,
      };

      const response = await axios.request(config);
      console.log("API Response:", response.data);

      const rows = response.data.things || [];
      const formattedRows = rows.map((row) => ({
        date: row.dateRange,
        thingId: row.thingId,
        journeyName: row.journeyName,
        journeyType: row.journeyType,
        journeyTime: row.timings,
        numStops: row.stopCount,
        stopsInfo: row.stopsInfo || [],
      }));

      setReportData(formattedRows);
      openSuccessSB();
    } catch (error) {
      console.error("Fetch Report Error:", error);
      setErrorMessage("Error loading stops timing report");
      openErrorSB();
    } finally {
      setLoading(false);
    }
  };

  const exportToCSV = () => {
    if (reportData.length === 0) {
      setErrorMessage("No data to export");
      openErrorSB();
      return;
    }
    const headers = ["Date", "Vehicle Number", "Journey Name", "Journey Type", "Journey Time", "Number of Stops"];
    const rows = reportData.map((row) =>
      [row.date, row.thingId, row.journeyName, row.journeyType, row.journeyTime, row.numStops].join(",")
    );
    const csvContent = [headers.join(","), ...rows].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `StopTimingReport.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const clearFilters = () => {
    setVehicleIds([]);
    setReportData([]);
    setErrorMessage("");
    setSelectedDate(today);
    setSelectedRow(null);
    setStopDetails([]);
  };

  const handleCloseDialog = () => {
    setSelectedRow(null);
    setStopDetails([]);
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox p={3} pb={0}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={4}>
                      <Autocomplete
                        multiple
                        options={things || []}
                        getOptionLabel={(option) => option.thingId}
                        value={things.filter((t) => vehicleIds.includes(t.thingId))}
                        onChange={(event, newValue) => {
                          setVehicleIds(newValue.map((v) => v.thingId));
                        }}
                        renderOption={(props, option) => (
                          <li {...props} style={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
                            <span>{option.nickName}</span>
                            <span style={{ color: "#666" }}>{option.thingId}</span>
                          </li>
                        )}
                        renderInput={(params) => (
                          <TextField {...params} placeholder="Select Vehicles" fullWidth size="small" />
                        )}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                      <DatePicker
                        label="Select Date"
                        value={selectedDate}
                        onChange={(newValue) => setSelectedDate(newValue)}
                        renderInput={(params) => <TextField {...params} fullWidth size="small" />}
                      />
                    </Grid>

                    <Grid item xs={4} sm={2} md={1}>
                      <MDButton fullWidth variant="gradient" color="info" size="small" onClick={fetchReport} disabled={loading}>
                        {loading ? <CircularProgress size={20} color="inherit" /> : "LOAD"}
                      </MDButton>
                    </Grid>

                    <Grid item xs={4} sm={2} md={1}>
                      <DownloadExcelButton fullWidth label="CSV" onClick={exportToCSV} disabled={loading} />
                    </Grid>

                    <Grid item xs={4} sm={2} md={1}>
                      <MDButton fullWidth variant="outlined" color="error" size="small" onClick={clearFilters} disabled={loading}>
                        CLEAR
                      </MDButton>
                    </Grid>
                  </Grid>
                </LocalizationProvider>
              </MDBox>

              {errorMessage && (
                <MDBox px={2} pt={2}>
                  <Alert severity="error">{errorMessage}</Alert>
                </MDBox>
              )}

              <MDBox pt={3} display="flex" justifyContent="center">
                {loading ? (
                  <CircularProgress />
                ) : (
                  <StopTimingReportTable
                    table={{ columns, rows: reportData }}
                    isSorted={true}
                    entriesPerPage={false}
                    showTotalEntries={true}
                    noEndBorder
                  />
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      <Dialog open={!!selectedRow} onClose={handleCloseDialog} maxWidth="md" fullWidth>
  <DialogTitle>
    Stop Reach Info for {selectedRow?.journeyName}
  </DialogTitle>

  <DialogContent>
    <StopTable
      table={{ columns: stopColumns, rows: stopDetails }}
      entriesPerPage={{ defaultValue: 5, entries: [5, 10, 20] }}
      canSearch
      showTotalEntries
      isSorted
      noEndBorder
      pagination={{ variant: "gradient", color: "info" }}
    />
  </DialogContent>

  {/* 👇 Add Close button here */}
  <DialogActions>
    <MDButton onClick={handleCloseDialog} variant="contained" color="primary">
      Close
    </MDButton>
  </DialogActions>
</Dialog>


      <MDSnackbar color="success" icon="check" title="Stop Timing Report" content="Data Loaded Successfully" open={successSB} onClose={closeSuccessSB} close={closeSuccessSB} bgWhite />
      <MDSnackbar color="error" icon="warning" title="Stop Timing Report" content="Failed to load report" open={errorSB} onClose={closeErrorSB} close={closeErrorSB} bgWhite />
    </DashboardLayout>
  );
}

// Helper function to convert milliseconds to HH:MM:SS
const msToTime = (duration) => {
  let seconds = Math.floor((duration / 1000) % 60);
  let minutes = Math.floor((duration / (1000 * 60)) % 60);
  let hours = Math.floor((duration / (1000 * 60 * 60)));

  hours = hours < 10 ? "0" + hours : hours;
  minutes = minutes < 10 ? "0" + minutes : minutes;
  seconds = seconds < 10 ? "0" + seconds : seconds;

  return `${hours}:${minutes}:${seconds}`;
};

// PropTypes definition
StopTimingReport.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      numStops: PropTypes.number,
      stopsInfo: PropTypes.arrayOf(
        PropTypes.shape({
          sequence: PropTypes.number,
          fenceName: PropTypes.string,
          entryTime: PropTypes.string,
          exitTime: PropTypes.string,
          timeInside: PropTypes.number,
        })
      ),
    }),
  }),
};

export default StopTimingReport;
